import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  Modal,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";

interface GameRecoveryModalProps {
  visible: boolean;
  onRetry?: () => void;
  onCancel?: () => void;
  autoRetry?: boolean;
  maxRetries?: number;
}

export const GameRecoveryModal: React.FC<GameRecoveryModalProps> = ({
  visible,
  onRetry,
  onCancel,
  autoRetry = true,
  maxRetries = 5,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!visible) {
      setRetryCount(0);
      setCountdown(5);
      return;
    }

    if (!autoRetry) return;
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (retryCount < maxRetries) {
            setRetryCount((c) => c + 1);
            onRetry?.();
            return 5; // Reset countdown
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [visible, retryCount, autoRetry, maxRetries, onRetry]);

  if (!visible) return null;

  const canRetry = retryCount < maxRetries;

  return (
    <Modal transparent visible={visible} animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <ActivityIndicator size="large" color="#FFD700" />
          
          <Text style={styles.title}>Game Recovering</Text>
          
          <Text style={styles.message}>
            Service is temporarily unavailable.
            {"\n"}Please wait while we reconnect...
          </Text>

          {autoRetry && canRetry && (
            <Text style={styles.retryInfo}>
              Retry attempt {retryCount + 1}/{maxRetries}
              {"\n"}Next retry in {countdown}s...
            </Text>
          )}

          {!canRetry && (
            <Text style={styles.message}>
              Almost there...
              {"\n"}
            </Text>
          )}

          <View style={styles.buttonContainer}>
            {canRetry && !autoRetry && (
              <TouchableOpacity
                style={styles.retryButton}
                onPress={() => {
                  setRetryCount((c) => c + 1);
                  onRetry?.();
                }}
              >
                <Text style={styles.buttonText}>Retry Now</Text>
              </TouchableOpacity>
            )}

            {/* <TouchableOpacity
              style={styles.cancelButton}
              onPress={onCancel}
            >
              <Text style={styles.buttonText}>Go to Lobby</Text>
            </TouchableOpacity> */}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    backgroundColor: "#2a2a2a",
    borderRadius: 20,
    padding: 30,
    width: "80%",
    maxWidth: 400,
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#FFD700",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFD700",
    marginTop: 20,
    marginBottom: 10,
  },
  message: {
    fontSize: 16,
    color: "#fff",
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 24,
  },
  retryInfo: {
    fontSize: 14,
    color: "#aaa",
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 20,
  },
  errorText: {
    fontSize: 14,
    color: "#ff6b6b",
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 20,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 10,
  },
  retryButton: {
    backgroundColor: "#4CAF50",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
  },
  cancelButton: {
    backgroundColor: "#666",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "bold",
  },
});